import { useMemo } from "react";

import { useIntl } from "react-intl";

import { Editions, ProductKeys } from "@atlassiansox/asc-stargate";

import { useCrossFlowFrontendTouchpoint } from "@/components/cross-flow/touchpoint";
import { useLocalizedHref } from "@/libraries/web-framework";
import { LogoV2 } from "@/libraries/wpl/foundations/src";
import { ProductPageSubNav } from "@/patterns/global-nav/src";
import { GlobalNavLinkContentProps } from "@/patterns/global-nav/src/nav-links-container";
import { GlobalNavProvider } from "@/patterns/global-nav/src/nav-links-container";
import { DropdownContent } from "@/templates/software/jira/service-management/jira-service-management-software-page/src/sections/software-jira-service-management-navbarv2";
import { experiments, useExperiment } from "@/utils/experiment";
import {
    checkFeatureFlagGate,
    featureFlags
} from "@/utils/feature-flags/index.ts";

import { TemplateCategoriesDropdown } from "./template-categories-dropdown";

const JiraLogo = () => <LogoV2 brand="jira" type="logo" appearance="brand" />;

const JiraSoftwareNav = () => {
    const intl = useIntl();
    const signupLink = useLocalizedHref(
        "/try/cloud/signup?bundle=jira-software&edition=free"
    );
    const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
        productKey: ProductKeys.JIRA_SOFTWARE,
        edition: Editions.FREE,
        redirectUrl: signupLink
    });

    const solutionsDropdownStyles = useMemo(() => {
        return {
            width: "fit-content !important",
            margin: 0,
            transform: "translateX(-40%)"
        };
    }, []);

    // Teams Hrefs
    const marketingHref = useLocalizedHref("/jira/solutions/marketing");
    const engineeringHref = useLocalizedHref("/jira/solutions/engineering");
    const designHref = useLocalizedHref("/jira/solutions/design");
    const operationsHref = useLocalizedHref("/jira/solutions/operations");
    const itHref = useLocalizedHref("/jira/solutions/it");

    // Use Cases Hrefs
    const planningHref = useLocalizedHref("/jira/solutions/planning");
    const campaignManagementHref = useLocalizedHref(
        "/jira/solutions/campaign-management"
    );
    const agileProjectManagementHref = useLocalizedHref(
        "/jira/solutions/agile-project-management"
    );
    const programManagementHref = useLocalizedHref(
        "/jira/solutions/program-management"
    );

    //Company Size Hrefs
    const enterpriseHref = useLocalizedHref("/software/jira/enterprise");

    const solutionsDropdown = useMemo(
        () => (
            <DropdownContent
                contentSections={[
                    {
                        header: intl.formatMessage({
                            id: "software-jira.navbar.solutions.teams.header",
                            defaultMessage: "Teams"
                        }),
                        links: [
                            {
                                text: intl.formatMessage({
                                    id: "software-jira.navbar.solutions.teams.marketing",
                                    defaultMessage: "Marketing"
                                }),
                                href: marketingHref
                            },
                            {
                                text: intl.formatMessage({
                                    id: "software-jira.navbar.solutions.teams.engineering",
                                    defaultMessage: "Engineering"
                                }),
                                href: engineeringHref
                            },
                            {
                                text: intl.formatMessage({
                                    id: "software-jira.navbar.solutions.teams.design",
                                    defaultMessage: "Design"
                                }),
                                href: designHref
                            },
                            {
                                text: intl.formatMessage({
                                    id: "software-jira.navbar.solutions.teams.operations",
                                    defaultMessage: "Operations"
                                }),
                                href: operationsHref
                            },
                            {
                                text: intl.formatMessage({
                                    id: "software-jira.navbar.solutions.teams.it",
                                    defaultMessage: "IT"
                                }),
                                href: itHref
                            }
                        ]
                    },
                    {
                        header: intl.formatMessage({
                            id: "software-jira.navbar.solutions.useCases.header",
                            defaultMessage: "Use Cases"
                        }),
                        links: [
                            {
                                text: intl.formatMessage({
                                    id: "software-jira.navbar.solutions.useCases.planning",
                                    defaultMessage: "Planning"
                                }),
                                href: planningHref
                            },
                            {
                                text: intl.formatMessage({
                                    id: "software-jira.navbar.solutions.useCases.campaign-management",
                                    defaultMessage: "Campaign Management"
                                }),
                                href: campaignManagementHref
                            },
                            {
                                text: intl.formatMessage({
                                    id: "software-jira.navbar.solutions.useCases.agile-project-management",
                                    defaultMessage: "Agile Project Management"
                                }),
                                href: agileProjectManagementHref
                            },
                            {
                                text: intl.formatMessage({
                                    id: "software-jira.navbar.solutions.useCases.program-management",
                                    defaultMessage: "Program Management"
                                }),
                                href: programManagementHref
                            }
                        ]
                    },
                    {
                        header: intl.formatMessage({
                            id: "software-jira.navbar.solutions.companySize.header",
                            defaultMessage: "Company Size"
                        }),
                        links: [
                            {
                                text: intl.formatMessage({
                                    id: "software-jira.navbar.solutions.companySize.enterprise",
                                    defaultMessage: "Enterprise"
                                }),
                                href: enterpriseHref
                            }
                        ]
                    }
                ]}
            />
        ),
        [
            intl,
            marketingHref,
            engineeringHref,
            designHref,
            operationsHref,
            itHref,
            planningHref,
            campaignManagementHref,
            agileProjectManagementHref,
            programManagementHref,
            enterpriseHref
        ]
    );

    const featuresHref = useLocalizedHref("/software/jira/features");
    const productGuideHref = useLocalizedHref(
        "/software/jira/guides/getting-started/introduction"
    );
    const templatesHref = useLocalizedHref("/software/jira/templates");
    const pricingHref = useLocalizedHref("/software/jira/pricing");
    const productLogoHref = useLocalizedHref("/software/jira");

    const templateCategoriesShown = checkFeatureFlagGate(
        featureFlags.jiraTemplatesDxpMigration
    );
    const templatesLinkContent = useMemo<GlobalNavLinkContentProps>(() => {
        const common = {
            globalLinksKey: "templates",
            linkText: intl.formatMessage({
                id: "software-jira.navbar.templates",
                defaultMessage: "Templates"
            }),
            productKey: "jira"
        };

        return templateCategoriesShown
            ? {
                  ...common,
                  hasSubnav: true,
                  content: <TemplateCategoriesDropdown />,
                  dropdownType: "templates",
                  containerPosition: "relative"
              }
            : {
                  ...common,
                  hasSubnav: false,
                  href: templatesHref
              };
    }, [templatesHref, templateCategoriesShown]);

    const newLinks: GlobalNavLinkContentProps[] = useMemo(
        () => [
            {
                globalLinksKey: "features",
                linkText: intl.formatMessage({
                    id: "software-jira.navbar.features",
                    defaultMessage: "Features"
                }),
                hasSubnav: false,
                productKey: "jira",
                href: featuresHref
            },
            {
                globalLinksKey: "solutions",
                linkText: intl.formatMessage({
                    id: "software-jira.navbar.solutions",
                    defaultMessage: "Solutions"
                }),
                hasSubnav: true,
                productKey: "jira",
                containerPosition: "relative",
                containerStyles: solutionsDropdownStyles,
                dropdownType: "solutions",
                content: solutionsDropdown
            },
            {
                globalLinksKey: "productGuide",
                linkText: intl.formatMessage({
                    id: "software-jira.navbar.product-guide",
                    defaultMessage: "Product guide"
                }),
                hasSubnav: false,
                productKey: "jira",
                href: productGuideHref
            },
            templatesLinkContent,
            {
                globalLinksKey: "pricing",
                linkText: intl.formatMessage({
                    id: "software-jira.navbar.pricing",
                    defaultMessage: "Pricing"
                }),
                hasSubnav: false,
                productKey: "jira",
                href: pricingHref
            }
        ],
        [
            intl,
            featuresHref,
            solutionsDropdown,
            solutionsDropdownStyles,
            productGuideHref,
            templatesHref,
            pricingHref
        ]
    );

    const links = useMemo(
        () => [
            {
                globalLinksKey: "features",
                linkText: intl.formatMessage({
                    id: "software-jira.navbar.features",
                    defaultMessage: "Features"
                }),
                hasSubnav: false,
                productKey: "jira",
                href: featuresHref
            },
            {
                globalLinksKey: "productGuide",
                linkText: intl.formatMessage({
                    id: "software-jira.navbar.product-guide",
                    defaultMessage: "Product guide"
                }),
                hasSubnav: false,
                productKey: "jira",
                href: productGuideHref
            },
            templatesLinkContent,
            {
                globalLinksKey: "pricing",
                linkText: intl.formatMessage({
                    id: "software-jira.navbar.pricing",
                    defaultMessage: "Pricing"
                }),
                hasSubnav: false,
                productKey: "jira",
                href: pricingHref
            },
            {
                globalLinksKey: "enterprise",
                linkText: intl.formatMessage({
                    id: "software-jira.navbar.enterprise",
                    defaultMessage: "Enterprise"
                }),
                hasSubnav: false,
                productKey: "jira",
                href: enterpriseHref
            }
        ],
        [
            enterpriseHref,
            featuresHref,
            intl,
            pricingHref,
            productGuideHref,
            templatesHref
        ]
    );

    const globalNavItemKeys = useMemo(() => {
        return links.map((link) => link.globalLinksKey);
    }, [links]);

    return (
        <GlobalNavProvider globalNavItemKeys={globalNavItemKeys}>
            <ProductPageSubNav
                productLogoUrl={productLogoHref}
                productLogo={<JiraLogo />}
                productLogoAriaLabel={intl.formatMessage({
                    id: "wacJiraSoftwareNavigation.logo-label",
                    defaultMessage: "Jira Software logo"
                })}
                mobileCtaText={intl.formatMessage({
                    id: "JiraeSoftwarePageTemplate.navbar.getitfree",
                    defaultMessage: "Get it free"
                })}
                mobileCtaUrl={signupLink}
                mobileCtaOnClick={openCrossFlow}
                mobileLogo={<JiraLogo />}
                navigationTrynowCtaText={intl.formatMessage({
                    id: "JiraFeatureSoftwarePageTemplate.heroCTA",
                    defaultMessage: "Get it free"
                })}
                navigationTrynowCtaUrl={signupLink}
                navigationTrynowCtaOnClick={openCrossFlow}
                productPageNavLinks={newLinks}
                mobileLinksSections={[
                    {
                        mobileLinkKey: "mobile-jira",
                        mobileLinksProps: newLinks
                    }
                ]}
                additionalMenuPageObjectsArray={[
                    {
                        menuPageKey: "solutions",
                        menuPageObjects: newLinks.find(
                            (link) => link.globalLinksKey === "solutions"
                        )?.content as JSX.Element
                    },
                    {
                        menuPageKey: "templates",
                        menuPageObjects: newLinks.find(
                            (link) => link.globalLinksKey === "templates"
                        )?.content as JSX.Element
                    }
                ]}
            />
        </GlobalNavProvider>
    );
};

JiraSoftwareNav.displayName = "JiraSoftwareNav";

export default JiraSoftwareNav;
